import { z } from "zod";
import { canViewHorseDetails, } from "../membership/authorization-policy.js";
export const HORSE_LIFECYCLE_STATUSES = [
    "ACTIVE",
    "RETIRED",
    "DECEASED",
];
export const HorseLifecycleStatusSchema = z.enum(HORSE_LIFECYCLE_STATUSES);
export const HORSE_RECORD_STATES = ["ACTIVE", "INACTIVE"];
export const HorseRecordStateSchema = z.enum(HORSE_RECORD_STATES);
export const HORSE_INACTIVE_REASONS = [
    "TRANSFER_GRACE",
    "CAPACITY_LIMIT",
];
export const HorseInactiveReasonSchema = z.enum(HORSE_INACTIVE_REASONS);
/**
 * Legal Horse lifecycle transitions. Deceased is terminal: `docs/products/
 * stable.md` and `COMPANY.md` never describe reviving a Deceased horse.
 */
const LEGAL_LIFECYCLE_TRANSITIONS = {
    ACTIVE: ["RETIRED", "DECEASED"],
    RETIRED: ["ACTIVE", "DECEASED"],
    DECEASED: [],
};
export function isLegalLifecycleTransition(from, to) {
    return from === to || LEGAL_LIFECYCLE_TRANSITIONS[from].includes(to);
}
/**
 * Retired and Deceased horses stop accepting new Weight/Temperature/Feed/
 * Training/Vet/Farrier/Accounting-allocation/Stopwatch-to-training entries;
 * an Inactive record state freezes new activity regardless of lifecycle
 * (ARCHITECTURE.md §13.3, §3.4).
 */
export function canAddSealedRecord(horse) {
    return horse.lifecycleStatus === "ACTIVE" && horse.recordState === "ACTIVE";
}
/** Deceased additionally blocks new Marketplace listings; Retired does not. */
export function canCreateMarketplaceListing(horse) {
    return horse.lifecycleStatus !== "DECEASED" && horse.recordState === "ACTIVE";
}
/** Deceased additionally blocks Stable Transfer initiation; Retired does not. */
export function canInitiateStableTransfer(horse) {
    return horse.lifecycleStatus !== "DECEASED" && horse.recordState === "ACTIVE";
}
/** A Stopwatch run attaches to a Horse's Training timeline like any other
 * Sealed record entry. */
export function canAttachStopwatchRun(horse) {
    return canAddSealedRecord(horse);
}
/**
 * Only authorized origin-Stable members may view a frozen (Inactive) Horse's
 * history; the destination Stable of a transfer gains no automatic access
 * (ARCHITECTURE.md §13.6, §13.12: "destination Stable and linked Owners
 * receive no automatic access"). As of Phase 10 (Stable Transfer), this is
 * satisfied by construction, not by a recordState check here: a Horse row
 * never changes `stableId` on transfer — the origin row (now frozen
 * `INACTIVE`/`TRANSFER_GRACE`) stays under the origin Stable, and the
 * destination gets an entirely separate fresh Horse row/id under its own
 * `stableId` (`respondStableTransfer`). Every Sealed-record read use case is
 * already scoped by `(stableId, horseId)` matching the *origin* Stable's
 * membership, so a destination-Stable actor has no membership row that
 * resolves against the origin horse at all, regardless of recordState. This
 * function is therefore identical to {@link canViewHorseDetails} in
 * behavior — any current member with at least VIEW on Horse Details — but
 * is the semantically canonical gate for reading a Horse's (possibly
 * frozen) history, and `getHorse` calls it explicitly for that reason.
 */
export function canViewFrozenHistory(membership) {
    return canViewHorseDetails(membership);
}
/**
 * Gelding is a dated, one-way lifecycle event: once Sex is GELDING it can
 * never be reverted through a normal edit (`docs/products/stable.md`).
 */
export function canRecordGelding(currentSex) {
    return currentSex !== "GELDING";
}
export function isLegalSexEdit(currentSex, requestedSex) {
    if (currentSex === "GELDING")
        return requestedSex === "GELDING";
    return true;
}
